import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import queryString from 'query-string';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import Section from 'components/Elements/Section';
import HeaderVideo from 'components/Elements/HeaderVideo';
import CheckoutButton from 'components/CheckoutButton';
import CountdownTimer from 'components/Elements/CountdownTimer';
import {SalesDisclaimer} from 'components/SalesCta';
import SalesCopy from 'components/SalesCopy';
import ModalExitSales from 'components/Modals/ModalExitSales';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';
import classNames from 'classnames';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = true,
  ctaOverride = null,
  timerOverride = null,
  videoUrlOverride = null,
  videoThumbnailOverride = null,
  ctaNoTimer = false,
  variant = null,
  ...props
}) {
  const {bucket, email, registrationId} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {t} = qs.t ? qs : {};

  const pageTitle = 'Join Tackling Type 2!';
  const pageDescription = 'Get everything you need to stay healthy inside Tackling Type 2.';
  const ogImage = 'https://cdn.foodrevolution.org/tt2/og/tt2-evergreen-opengraph-sales-1200x1200-v20220804.png';
  const shareUrl = 'https://type2.foodrevolution.org/join/';

  const isCartClose = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime() < new Date().getTime();
  const salesEnd = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();
  const salesTimerDate = timerOverride ? timerOverride : salesEnd;

  const salesFullPrice =
    (!email && !registrationId && !ctaOverride) ||
    (!ctaOverride && webinarSchedule.phase === 'after') ||
    ctaOverride === 'sales497';

  const productID = 'C-TT2';
  const productSku = 'C-TT2';
  const productName = 'Tackling Type 2';
  const productSlug = t || !salesFullPrice ? 'tt2-special' : 'tt2';
  const productValue = t || !salesFullPrice ? 297 : 497;
  const paymentPlanPrice = t || !salesFullPrice ? 109 : 179;

  useEffect(() => {
    track('Product Viewed', {
      product_id: productID,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  }, [salesFullPrice]);

  const cartCloseVideo = {
    prevent: 'https://www.youtube.com/watch?v=zTgwCdiMOCc',
    optimize: 'https://www.youtube.com/watch?v=zTgwCdiMOCc',
    reverse: 'https://www.youtube.com/watch?v=YptfECNd08k'
  }[bucket];
  const cartCloseVideoThumb = 'https://cdn.foodrevolution.org/tt2/videothumb/1154221199-thumb.jpg';

  const salesPriceVideo = {
    prevent: 'https://www.youtube.com/watch?v=h5G2J45mo-o',
    optimize: 'https://www.youtube.com/watch?v=h5G2J45mo-o',
    reverse: 'https://www.youtube.com/watch?v=8jqLV4Z73sA'
  }[bucket];
  const salesPriceVideoThumb = 'https://cdn.foodrevolution.org/tt2/videothumb/1357522236-thumb.jpg';

  const fullPriceVideo = {
    prevent: 'https://www.youtube.com/watch?v=bpOGfDYeDho',
    optimize: 'https://www.youtube.com/watch?v=bpOGfDYeDho',
    reverse: 'https://www.youtube.com/watch?v=v-2JrwMJ6vk'
  }[bucket];
  const fullPriceVideoThumb = 'https://cdn.foodrevolution.org/tt2/videothumb/1789299492-thumb.jpg';

  useBeacon();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <Section id="join-header-cta" color="black" className="section-angle-first text-center text-white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">
                  {
                    {
                      prevent: (
                        <>The critical question: Are you sure you’re doing everything you can to prevent diabetes?</>
                      ),
                      optimize: (
                        <>The critical question: Are you sure you’re doing everything you can to reverse prediabetes?</>
                      ),
                      reverse: (
                        <>The critical question: Are you sure you’re doing everything you can to reverse diabetes?</>
                      )
                    }[bucket]
                  }
                </h2>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col lg="10" className="video-col px-0 px-sm-3">
                <h5 className="section-heading">Watch this video to learn the truth:</h5>
                <HeaderVideo
                  url={
                    videoUrlOverride
                      ? videoUrlOverride
                      : t || !salesFullPrice
                      ? isCartClose
                        ? cartCloseVideo
                        : salesPriceVideo
                      : fullPriceVideo
                  }
                  thumbnail={
                    videoThumbnailOverride
                      ? videoThumbnailOverride
                      : t || !salesFullPrice
                      ? isCartClose
                        ? cartCloseVideoThumb
                        : salesPriceVideoThumb
                      : fullPriceVideoThumb
                  }
                  noFixed={noFixedVideo}
                  sku={productSku}
                  slug={productSlug}
                  price={productValue}
                  label="TT2 - Sales"
                  sales
                />
              </Col>
            </Row>
            <Row className="mt-5">
              <Col>
                <h4 className="mt-0">Brought to You By Brenda Davis, RD, and the Team at Food Revolution Network</h4>
                <img
                  className="img-course-logo mb-4"
                  src="https://cdn.foodrevolution.org/tt2/tt2-logo-202401-white.svg"
                />
                <p>Get immediate access to the groundbreaking program</p>
                <div className="header-cta-wrap mt-5">
                  {t || !salesFullPrice ? (
                    <>
                      <h2 className="section-heading mb-3">
                        Right Now Just <del>$497</del> $297
                      </h2>
                      {!ctaNoTimer ? (
                        <>
                          <h5 className="mt-4">Your discount expires in:</h5>
                          <CountdownTimer time={salesTimerDate} className="countdown-light" />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <h2 className="section-heading">Right Now $497</h2>
                    </>
                  )}
                  <CheckoutButton sku={productSku} price={productValue} slug={productSlug} className="my-3" />
                  <SalesDisclaimer iconColor="white" />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <SalesCopy
          version="sales"
          productId={productID}
          productSku={productSku}
          productSlug={productSlug}
          productPrice={productValue}
          paymentPlanPrice={paymentPlanPrice}
          variant={variant}
          salesFullPrice={salesFullPrice}
          ctaOverride={ctaOverride}
          ctaNoTimer={ctaNoTimer}
          timerOverride={timerOverride}
        />
      </div>

      {noExitIntent ? null : <ModalExitSales version={t || !salesFullPrice ? 'sales' : 'full'} />}

      <div className="footer-content mt-auto">
        <Footer />
        <ShareButtons
          shareUrl={shareUrl}
          shareImg={ogImage}
          facebookDescription="The mainstream Type 2 diabetes advice is outdated — but thanks to the latest scientific breakthroughs, we now know exactly what to eat and do. Get to the real root with all-natural food and lifestyle tips to feel better fast. Learn more here:"
          twitterDescription="Get to the real root of #diabetes with all-natural food and lifestyle tips to feel your best. Learn more about Tackling Type 2 here:"
          twitterImg="https://cdn.foodrevolution.org/tt2/og/tt2-evergreen-opengraph-sales-1200x675-v20220804.png"
          pinterestDescription="The mainstream Type 2 diabetes advice is outdated — but thanks to the latest scientific breakthroughs, we now know exactly what to eat and do. Get to the real root with all-natural food and lifestyle tips to feel better fast. Learn more here:"
          pinterestImg="https://cdn.foodrevolution.org/tt2/og/tt2-evergreen-opengraph-sales-1000x1500-v20220804.png"
          floating
        />
      </div>
    </div>
  );
}
